import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Col, Row, Spin, Alert } from "antd";
import LayoutUserLoggedOut from "../navigation/LayoutUserLoggedOut";
import logo from "../navigation/alplus-logo.png";

const API_BASE = process.env.REACT_APP_API_URI;

const maskEmail = (email) => {
  if (!email) return "";
  const atIndex = email.indexOf("@");
  if (atIndex < 0) return email;
  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex);
  const visible = local.slice(0, 2);
  return `${visible}*****${domain}`;
};

const maskPhone = (phone) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  const last4 = digits.slice(-4);
  return `******${last4}`;
};

const maskPolicy = (policy) => {
  if (!policy) return "";
  return `***${policy.slice(3)}`;
};

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const UnsubscribePage = () => {
  const { id } = useParams();
  const isRequestId = GUID_RE.test(id ?? "");
  const queryParam = isRequestId ? `requestId=${id}` : `renewalKey=${id}`;
  const bodyParam = isRequestId ? { requestId: id } : { renewalKey: parseInt(id, 10) };

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    fetch(`${API_BASE}/Reminder/GetUnsubscribeInfo?${queryParam}`)
      .then((res) => {
        if (res.status === 404) {
          setNotFound(true);
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (data) setInfo(data);
      })
      .catch(() => setError("Unable to load unsubscribe information."))
      .finally(() => setLoading(false));
  }, [id]);

  const handleUnsubscribe = () => {
    setSubmitting(true);
    fetch(`${API_BASE}/Reminder/Unsubscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(bodyParam),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setDone(true);
      })
      .catch(() => setError("Something went wrong. Please try again."))
      .finally(() => setSubmitting(false));
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Row>
          <Col span={24} className="centered-box" style={{ marginTop: 36 }}>
            <Spin size="large" />
          </Col>
        </Row>
      );
    }

    if (notFound) {
      return (
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <div className="fixed-sized-box instructions-message centered-message">
              This unsubscribe link is invalid or has expired.
            </div>
          </Col>
        </Row>
      );
    }

    if (error) {
      return (
        <Row justify="center" style={{ marginTop: 36 }}>
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <Alert message="Error" description={error} type="error" showIcon />
          </Col>
        </Row>
      );
    }

    if (done) {
      return (
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <div className="fixed-sized-box instructions-message centered-message">
              You have been unsubscribed. You will no longer receive email or
              text reminder messages.
            </div>
          </Col>
        </Row>
      );
    }

    const hasPhone = !!(info?.CustomerPhone || info?.PhoneNumber);
    const phoneDisplay = info?.CustomerPhone || info?.PhoneNumber;

    return (
      <>
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <div
              className="fixed-sized-box instructions-message centered-message"
              style={{ padding: "16px 24px", lineHeight: "1.8" }}
            >
              {info && (
                <>
                  <div>
                    You are unsubscribing the following contact(s) from reminders
                    for policy <strong>{maskPolicy(info.PolicyNumber)}</strong>:
                  </div>
                  {info.CustomerEmail && (
                    <div style={{ marginTop: 8 }}>
                      <strong>Email:</strong> {maskEmail(info.CustomerEmail)}
                    </div>
                  )}
                  {hasPhone && (
                    <div style={{ marginTop: 4 }}>
                      <strong>Phone:</strong> {maskPhone(phoneDisplay)}
                    </div>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
        <Row style={{ marginTop: 36 }}>
          <Col span={24} className="centered-box">
            <Button
              type="primary"
              className="fixed-sized-box login-button"
              loading={submitting}
              onClick={handleUnsubscribe}
            >
              Unsubscribe
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <LayoutUserLoggedOut>
      <Row>
        <Col span={24}>
          <Row>
            <Col span={4} offset={10}>
              <div className="login-image-sizing">
                <img
                  className="logoImageLoading"
                  src={logo}
                  alt="AutolinkPlus logo"
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={24} className="login-sub-message">
              The preferred auto insurance management software by brokers in
              British Columbia
            </Col>
          </Row>
          <Row style={{ marginTop: 14 }}>
            <Col span={24} className="centered-box">
              <div className="tab-box-parent fixed-sized-box">
                <div className="tab-box-child">
                  <div className="tab-box-container">Unsubscribe</div>
                </div>
              </div>
            </Col>
          </Row>
          {renderContent()}
        </Col>
      </Row>
    </LayoutUserLoggedOut>
  );
};

export default UnsubscribePage;
